/** 時刻・数値・選択・自由記述の共通入力。 */
import { useId, useState } from 'react';
import { formatTime } from '@meisui/core';
import { isInvalidTimeInput, normalizeTimeInput } from '../lib/time-input.js';
import { Modal } from './Modal.js';

export function TimeField({
  label,
  text,
  onTextChange,
  disabled = false,
  hint,
  error,
  showErrors = false,
  compact = false,
}: {
  label: string;
  text: string;
  onTextChange: (text: string) => void;
  disabled?: boolean;
  hint?: string;
  error?: string;
  showErrors?: boolean;
  compact?: boolean;
}): React.JSX.Element {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const [selection, setSelection] = useState<number | null>(null);
  const invalid = isInvalidTimeInput(text);
  const message =
    (touched || showErrors) && invalid
      ? '時刻を確認してください（例：08:30、翌日は25:30）。'
      : error;
  const minutes = normalizeTimeInput(text);
  const commit = (): void => {
    setTouched(true);
    if (!invalid) onTextChange(minutes === null ? '' : formatTime(minutes));
  };
  const choose = (value: number | null): void => {
    onTextChange(value === null ? '' : formatTime(value));
    setTouched(true);
    setSelection(null);
  };
  return (
    <div className={compact ? 'min-w-32' : ''}>
      <label htmlFor={id} className={compact ? 'sr-only' : 'field-label'}>
        {label}
      </label>
      <div className="flex items-stretch gap-1">
        <input
          id={id}
          className={`input min-w-0 ${message ? 'input--error' : ''}`}
          type="text"
          inputMode="numeric"
          placeholder="08:30"
          value={text}
          disabled={disabled}
          aria-invalid={Boolean(message)}
          aria-describedby={`${id}-hint ${id}-error`}
          onChange={(event) => onTextChange(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
        <button
          type="button"
          className="btn btn--secondary time-picker-trigger"
          disabled={disabled}
          aria-label={`${label}を選択`}
          aria-haspopup="dialog"
          onClick={() => setSelection(minutes ?? 8 * 60)}
        >
          ▾
        </button>
      </div>
      <span id={`${id}-hint`} className="field-hint">
        {minutes !== null && minutes >= 1440 && (
          <span className="block font-semibold text-brand-700">
            翌日 {formatTime(minutes - 1440)}
          </span>
        )}
        {!compact && hint}
      </span>
      <span
        id={`${id}-error`}
        className="block text-xs text-danger-600"
        role={message ? 'alert' : undefined}
      >
        {message}
      </span>
      {selection !== null && (
        <Modal title={`${label}を選択`} onClose={() => setSelection(null)} sheet>
          <div className="space-y-4">
            <div className="flex gap-2" role="group" aria-label="日付区分">
              {[0, 1].map((day) => (
                <button
                  type="button"
                  key={day}
                  className={`btn flex-1 ${Math.floor(selection / 1440) === day ? 'btn--primary' : 'btn--secondary'}`}
                  aria-pressed={Math.floor(selection / 1440) === day}
                  onClick={() => setSelection((selection % 1440) + day * 1440)}
                >
                  {day === 0 ? '当日' : '翌日'}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className="field-label">時</span>
                <select
                  className="input"
                  aria-label="時"
                  value={Math.floor(selection / 60) % 24}
                  onChange={(event) =>
                    setSelection(
                      Math.floor(selection / 1440) * 1440 +
                        Number(event.target.value) * 60 +
                        (selection % 60),
                    )
                  }
                >
                  {Array.from({ length: 24 }, (_, hour) => (
                    <option key={hour} value={hour}>
                      {String(hour).padStart(2, '0')}時
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="field-label">分</span>
                <select
                  className="input"
                  aria-label="分"
                  value={selection % 60}
                  onChange={(event) =>
                    setSelection(Math.floor(selection / 60) * 60 + Number(event.target.value))
                  }
                >
                  {Array.from({ length: 60 }, (_, minute) => (
                    <option key={minute} value={minute}>
                      {String(minute).padStart(2, '0')}分
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-4 gap-2" role="group" aria-label="分の候補">
              {[0, 15, 30, 45].map((minute) => (
                <button
                  type="button"
                  className="btn btn--secondary"
                  key={minute}
                  onClick={() => setSelection(Math.floor(selection / 60) * 60 + minute)}
                >
                  {String(minute).padStart(2, '0')}分
                </button>
              ))}
            </div>
            <p className="text-sm text-ink-sub">
              1分単位で選べます。翌日の時刻は24時以降の表記で保存します。
            </p>
            <div className="flex justify-between gap-2">
              <button type="button" className="btn btn--ghost" onClick={() => choose(null)}>
                時刻を消す
              </button>
              <button type="button" className="btn btn--primary" onClick={() => choose(selection)}>
                {formatTime(selection)} を入力
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  unit,
  disabled = false,
  hint,
  min = 0,
  max,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  unit: string;
  disabled?: boolean;
  hint?: string;
  min?: number;
  max?: number;
}): React.JSX.Element {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="flex items-center gap-2">
        <input
          className="input"
          type="number"
          value={value ?? ''}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(event) =>
            onChange(event.target.value === '' ? null : Number(event.target.value))
          }
        />
        <span className="shrink-0 text-[0.8rem] text-ink-muted">{unit}</span>
      </div>
      {hint !== undefined && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
  hint,
  placeholder,
}: {
  label: string;
  value: T | null;
  options: readonly T[];
  onChange: (value: T | null) => void;
  disabled?: boolean;
  hint?: string;
  placeholder?: string;
}): React.JSX.Element {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <select
        className="input"
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value === '' ? null : (event.target.value as T))}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {hint !== undefined && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  disabled = false,
  hint,
  placeholder,
  multiline = false,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: string;
  placeholder?: string;
  multiline?: boolean;
  required?: boolean;
}): React.JSX.Element {
  return (
    <label className="block">
      <span className="field-label">
        {label}
        {required && <span className="ml-1 text-danger-600">*</span>}
      </span>
      {multiline ? (
        <textarea
          className="input"
          rows={2}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          className="input"
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {hint !== undefined && <span className="field-hint">{hint}</span>}
    </label>
  );
}
